import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Users';

// G-K-08 yaşam döngüsü durumları. IsActive bu alandan TÜRETİLİR; ikisi de listede durur çünkü
// eski kayıtlarda ve entegrasyonlarda hâlâ IsActive okunuyor.
// tone değerleri StatusBadge'in DESTEKLEDİKLERİ ile sınırlı: ok · wait · bad · neutral.
const USER_STATUS = {
  1: { label: 'Aktif', tone: 'ok' },
  2: { label: 'Kilitli', tone: 'bad' },
  3: { label: 'Pasif', tone: 'neutral' },
  4: { label: 'İptal', tone: 'bad' },
};

const USER_TYPE = { 1: 'Normal', 2: 'Destek' };

const COLUMN_FILTER_META = {
  firstName: { type: 'text', odataField: 'FirstName' },
  lastName: { type: 'text', odataField: 'LastName' },
  email: { type: 'text', odataField: 'Email' },
  phoneNumber: { type: 'text', odataField: 'PhoneNumber' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }] },
  // G-K-10: yeni yaşam döngüsü alanları listede filtrelenebilir olmalı — aksi hâlde
  // "iptal edilmiş hesapları göster" gibi temel bir sorgu ekrandan yapılamaz.
  status: {
    type: 'select', odataField: 'Status', valueFormat: 'raw',
    options: Object.entries(USER_STATUS).map(([v, o]) => ({ value: v, label: o.label })),
  },
  userType: {
    type: 'select', odataField: 'UserType', valueFormat: 'raw',
    options: Object.entries(USER_TYPE).map(([v, l]) => ({ value: v, label: l })),
  },
};
const SEARCH_FIELDS = ['FirstName', 'LastName', 'Email', 'PhoneNumber'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

export function UserPage() {
  usePageMeta('Kullanıcı Tanımları', 'Kullanıcıları, rollerini ve durum bilgilerini tek ekrandan yönetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [roles, setRoles] = useState([]);
  // #199 userId → [roleId] eşlemesi. Liste OData sorgusu UserRole'ü $expand ETMEZ
  // (expand backend'de asılıyor) → rol kolonu için eşleme ayrı /api/UserRoles/Select'ten gelir.
  const [rolesByUser, setRolesByUser] = useState({});
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'FirstName',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Roller küçük bir lookup — tümünü /Select ile yükle (rol adı kolonu için).
  useEffect(() => {
    fetch('/api/Roles/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((json) => {
        const data = json?.data ?? json ?? [];
        setRoles(Array.isArray(data) ? data.filter((role) => role.isActive) : []);
      })
      .catch(() => {});
  }, []);

  // #199 Kullanıcı-rol eşlemesini ayrı çek (Users listesi $expand=UserRole yapmaz/yapamaz).
  useEffect(() => {
    fetch('/api/UserRoles/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((json) => {
        const data = json?.data ?? json ?? [];
        const map = {};
        (Array.isArray(data) ? data : []).forEach((ur) => {
          const uid = String(pick(ur, 'userId', 'UserId') ?? '');
          const rid = String(pick(ur, 'roleId', 'RoleId') ?? '');
          if (!uid || !rid) return;
          (map[uid] = map[uid] || []).push(rid);
        });
        setRolesByUser(map);
      })
      .catch(() => {});
  }, []);

  // Bir kullanıcının rol adlarını (birden fazla olabilir) virgülle birleştirir.
  const getUserRoleNames = useCallback((userId) => {
    const rids = rolesByUser[String(userId)] || [];
    const names = rids
      .map((rid) => roles.find((item) => String(item.id) === String(rid)))
      .filter(Boolean)
      .map((role) => role.name);
    return names.length ? names.join(', ') : '-';
  }, [roles, rolesByUser]);

  const openNew = () => navigate('/user-management/new');
  // #282 view=true → salt-okunur izle; edit → düzenlenebilir.
  const openDetail = (row, view = false) => navigate(`/user-management/${row.id}`, { state: { row, view } });
  // #282 kalem → düzenleme modu (DetailShell ?edit=1 ile açılır); göz/çift tık salt-okunur kalır.
  const openEditRow = (row) => navigate(`/user-management/${row.id}?edit=1`, { state: { row } });

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(selected),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Kullanıcı silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('firstName', { header: 'Ad', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('lastName', { header: 'Soyad', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('email', { header: 'E-posta', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('phoneNumber', { header: 'Telefon', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.display({
      id: 'roleName', header: 'Rol', enableColumnFilter: false, enableSorting: false,
      cell: ({ row }) => getUserRoleNames(row.original.id),
    }),
    // G-K-08: dört durumlu yaşam döngüsü. Status yoksa (migration öncesi kayıt) IsActive'e düşülür
    // — iki alan bir süre paralel yaşıyor, listenin ikisini de anlaması gerekiyor.
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const raw = info.getValue();
        const s = USER_STATUS[raw];
        if (s) return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
        return info.row.original.isActive
          ? <StatusBadge tone="ok">Aktif</StatusBadge>
          : <StatusBadge tone="bad">Pasif</StatusBadge>;
      },
    }),
    columnHelper.accessor('userType', {
      header: 'Tip', meta: { filterType: 'select' },
      // KK-15: destek kullanıcısı tek oturum kuralından muaftır ve çoklu kullanılır —
      // listede ayırt edilebilmesi denetim açısından önemli.
      cell: (info) => (info.getValue() === 2
        ? <StatusBadge tone="wait">Destek</StatusBadge>
        : 'Normal'),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original, true); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getUserRoleNames, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('FirstName', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'firstName', headerName: 'Ad' },
        { field: 'lastName', headerName: 'Soyad' },
        { field: 'email', headerName: 'E-posta' },
        { field: 'phoneNumber', headerName: 'Telefon' },
        { headerName: 'Rol', valueGetter: ({ row }) => getUserRoleNames(row.id) },
        // Status yoksa (migration öncesi kayıt) IsActive'e düşülür — iki alan paralel yaşıyor.
        { headerName: 'Durum', valueGetter: ({ row }) => USER_STATUS[row.status]?.label ?? (row.isActive ? 'Aktif' : 'Pasif') },
        { headerName: 'Tip', valueGetter: ({ row }) => USER_TYPE[row.userType] ?? 'Normal' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `kullanicilar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Ad, e-posta veya telefon ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kullanıcı Tanımları"
        subtitle="Kullanıcıları, rollerini ve durum bilgilerini tek ekrandan yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Kullanıcı</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => openDetail(row, true)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>{selected?.firstName} {selected?.lastName}</strong> kullanıcısını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
